import type { CalendarItem } from './calendar-item';
import type { LayerChoices } from './calendar-layers';
import type { CalendarLayer } from './calendar-sources';

export interface CalendarStaffMember {
  key: string;
  siteId: string;
  resourceId: string;
  name: string;
  /** The member is the reader themselves: their bookings sit in the Day view's own column, not a column of theirs. */
  mine?: boolean;
}

/** One person the Filters list offers, under their site. `key` is what a filter choice is stored by. */
export interface CalendarStaffEntry {
  key: string;
  name: string;
  /** The Bookings resource id the person's column and writes use. */
  resourceId: string;
  /** Bookings of this person in the period. */
  count: number;
  mine?: boolean;
}

export interface CalendarStaffGroup {
  siteId: string;
  /** The site's Bookings layer, or undefined before the layers are known (the site then counts as shown). */
  layerId?: string;
  /** The site's name; undefined when the site has none, and the caller names it the way it names the site's layer. */
  label?: string;
  staff: CalendarStaffEntry[];
}

/** Whether a site's staff can be chosen: its Bookings layer is on, or not known yet. */
export const isStaffGroupShown = (group: CalendarStaffGroup, shownLayers: Set<string>) => !group.layerId || shownLayers.has(group.layerId);

/** A person stays shown unless hidden on their own site. */
export const isStaffShown = (entry: Pick<CalendarStaffEntry, 'key'>, choices: LayerChoices) => choices[`staff:${entry.key}`] !== false;

const rosterKey = (siteId: string, resourceId: string) => `${siteId}:${resourceId}`;

/** The staff keys a booking belongs to, by resource id: the server sends a booking's staff names and ids together. */
export function bookingStaffKeys(item: CalendarItem): string[] {
  const siteId = item.sourceId ?? '';
  return (item.staffResourceIds ?? []).map((resourceId) => rosterKey(siteId, resourceId));
}

/** A booking stays unless every person on it has been hidden; a booking with nobody on it always stays. */
export function itemsOfShownStaff(items: CalendarItem[], shownStaff: Set<string>): CalendarItem[] {
  return items.filter((item) => {
    if (item.kind !== 'booking') return true;
    const keys = bookingStaffKeys(item);
    return keys.length === 0 || keys.some((key) => shownStaff.has(key));
  });
}

/**
 * The Filters list's staff, one group per Bookings site: the site's roster plus anyone its bookings name who is
 * not on it, each counted over the site's bookings in the period.
 */
export function calendarStaffGroups(members: CalendarStaffMember[], items: CalendarItem[], layers: CalendarLayer[]): CalendarStaffGroup[] {
  const bookings = items.filter((item) => item.kind === 'booking');
  const siteIds = [...new Set([...members.map((member) => member.siteId), ...bookings.map((item) => item.sourceId ?? '')])];
  return siteIds.map((siteId) => {
    const layer = layers.find((each) => each.id === `booking:${siteId}`);
    const entries = new Map<string, CalendarStaffEntry>();
    for (const member of members) {
      if (member.siteId === siteId) entries.set(rosterKey(siteId, member.resourceId), { key: rosterKey(siteId, member.resourceId), name: member.name, resourceId: member.resourceId, count: 0, ...(member.mine ? { mine: true } : {}) });
    }
    for (const item of bookings) {
      if ((item.sourceId ?? '') !== siteId) continue;
      bookingStaffKeys(item).forEach((key, index) => {
        // Someone the roster lacks (removed since, or a roster cut short): named from the booking, and still a column.
        const resourceId = item.staffResourceIds?.[index] ?? '';
        const entry = entries.get(key) ?? { key, name: item.staff?.[index] ?? resourceId, resourceId, count: 0 };
        entries.set(key, { ...entry, count: entry.count + 1 });
      });
    }
    return {
      siteId,
      layerId: layer?.id,
      label: layer?.label,
      staff: [...entries.values()].sort((a, b) => a.name.localeCompare(b.name)),
    };
  });
}

/** Every staff entry the groups offer, for the stored filter choices. */
export function staffEntriesOf(groups: CalendarStaffGroup[]): CalendarStaffEntry[] {
  return groups.flatMap((group) => group.staff);
}

/**
 * The Day view's staff columns: the staff of shown sites that the Filters list has not hidden, site by site.
 * The reader has no column of their own; their bookings are in the reader's column (`isMineItem`).
 */
export function calendarDayStaff(
  groups: CalendarStaffGroup[],
  shownLayers: Set<string>,
  shownStaff: Set<string>,
): { siteId: string; siteLabel?: string; key: string; resourceId: string; name: string }[] {
  return groups
    .filter((group) => isStaffGroupShown(group, shownLayers))
    .flatMap((group) =>
      group.staff
        .filter((entry) => !entry.mine && shownStaff.has(entry.key))
        .map((entry) => ({ siteId: group.siteId, siteLabel: group.label, key: entry.key, resourceId: entry.resourceId, name: entry.name })),
    );
}

/**
 * The site bands over the Day columns: one per run of consecutive columns of one site, keyed by the site so two
 * sites that share a name stay apart. None when the columns do not span two sites.
 */
export function staffColumnBands(
  columnSites: (string | undefined)[],
  { always = false }: { always?: boolean } = {},
): { siteId: string; start: number; span: number }[] {
  const bands: { siteId: string; start: number; span: number }[] = [];
  columnSites.forEach((siteId, index) => {
    if (siteId === undefined) return;
    const last = bands[bands.length - 1];
    if (last && last.siteId === siteId && last.start + last.span === index) last.span += 1;
    else bands.push({ siteId, start: index, span: 1 });
  });
  return always || new Set(bands.map((band) => band.siteId)).size > 1 ? bands : [];
}

/**
 * Whether an item belongs in the reader's own Day column: everything but the bookings that only shown staff with
 * columns of their own work. A booking with nobody on it, or with the reader on it, stays, so no shown booking is left
 * out of the Day view.
 */
export function isMineItem(item: CalendarItem, shownStaff: Set<string>, columnKeys: Set<string>): boolean {
  if (item.kind !== 'booking') return true;
  const keys = bookingStaffKeys(item);
  return keys.length === 0 || keys.some((key) => shownStaff.has(key) && !columnKeys.has(key));
}
